import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Download,
  Eye,
  FileText,
  FlaskConical,
  MoreVertical,
  Pill,
  Printer,
  Stethoscope,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { GetCurrentUser } from '@virtual-care/client';
import {
  type LocalizableSpeciality,
  formatDate,
  getLocalizedSpeciality,
} from '@virtual-care/isomorphic';
import { DownloadButton } from '@virtual-care/media-web';
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import {
  AppointmentReport,
  Loading,
  useAppointmentReportData,
  useData,
} from '@virtual-care/ui';

import { useLanguage } from '../../i18n/use-language.ts';
import { Header, ProfileCard, SearchComponent } from '../components/header';
import { useSetting } from '../hooks/use-settings.tsx';

type TabType = 'standard' | 'second_opinion' | 'homecare';

type MedicalRecord = {
  id: string;
  recordNumber: number;
  service: TabType;
  doctor: { firstName: string; lastName: string } | null;
  speciality: LocalizableSpeciality;
  providerName?: string;
  date: string;
  status: string;
  reportAvailability?: {
    prescription: boolean;
    labTests: boolean;
    diagnosis: boolean;
  };
};

export default function MedicalRecords() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const homecareEnabled = useSetting('homecareEnabled');
  const isHomecareEnabled = homecareEnabled?.value === 'true';
  const [activeTab, setActiveTab] = useState<TabType>('standard');

  const tabs: { key: TabType; label: string; enabled?: boolean }[] = [
    { key: 'standard', label: t('appointments.tabs.standard') },
    { key: 'second_opinion', label: t('appointments.tabs.secondOpinion') },
    {
      key: 'homecare',
      label: t('appointments.tabs.homecare'),
      enabled: isHomecareEnabled,
    },
  ];

  const [standardPagination, setStandardPagination] = useState<PaginationState>(
    { pageIndex: 0, pageSize: 10 },
  );
  const [soPagination, setSoPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [hcPagination, setHcPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [selectedRecord, setSelectedRecord] = useState<MedicalRecord | null>(
    null,
  );
  const [previewRecord, setPreviewRecord] = useState<MedicalRecord | null>(
    null,
  );
  const [shouldPrint, setShouldPrint] = useState(false);

  const { data: standardData, isLoading: isLoadingStandard } = useData(
    'GET /patients/me/reservations',
    {
      page: standardPagination.pageIndex + 1,
      pageSize: standardPagination.pageSize,
    },
    { placeholderData: keepPreviousData },
  );

  const { data: soData, isLoading: isLoadingSO } = useData(
    'GET /patients/me/second-opinions',
    {
      page: soPagination.pageIndex + 1,
      pageSize: soPagination.pageSize,
    },
    { placeholderData: keepPreviousData },
  );

  const { data: hcData, isLoading: isLoadingHC } = useData(
    'GET /patients/me/homecare-reservations',
    {
      page: hcPagination.pageIndex + 1,
      pageSize: hcPagination.pageSize,
    },
    { placeholderData: keepPreviousData, enabled: isHomecareEnabled },
  );

  const standardRecords: MedicalRecord[] = useMemo(
    () =>
      (standardData?.records || []).map((r) => ({
        id: r.id,
        recordNumber: r.reservationNumber,
        service: 'standard' as const,
        doctor: r.doctor,
        speciality: r.speciality,
        date: r.scheduledDate,
        status: r.status,
        reportAvailability: r.reportAvailability,
      })),
    [standardData?.records],
  );

  const soRecords: MedicalRecord[] = useMemo(
    () =>
      (soData?.records || []).map((r) => ({
        id: r.id,
        recordNumber: r.counter,
        service: 'second_opinion' as const,
        doctor: r.doctor,
        speciality: r.speciality,
        date: r.createdAt,
        status: r.status,
      })),
    [soData?.records],
  );

  const hcRecords: MedicalRecord[] = useMemo(
    () =>
      (hcData?.records || []).map((r) => {
        const vendorName =
          r.vendor.vendorType === 'INDIVIDUAL'
            ? `${r.vendor.individualProfile?.firstName ?? ''} ${r.vendor.individualProfile?.lastName ?? ''}`.trim() ||
              t('common.notSpecified')
            : (r.vendor.clinicProfile?.clinicName ?? t('common.notSpecified'));
        return {
          id: r.id,
          recordNumber: r.counter,
          service: 'homecare' as const,
          doctor: null,
          speciality: { name: r.vendorService.service.name },
          providerName: vendorName,
          date: r.scheduledDate,
          status: r.status,
        };
      }),
    [hcData?.records, t],
  );

  const activeRecords = {
    standard: standardRecords,
    second_opinion: soRecords,
    homecare: hcRecords,
  }[activeTab];
  const activePageCount =
    {
      standard: standardData?.pageCount,
      second_opinion: soData?.pageCount,
      homecare: hcData?.pageCount,
    }[activeTab] || 0;
  const activeLoading = {
    standard: isLoadingStandard,
    second_opinion: isLoadingSO,
    homecare: isLoadingHC,
  }[activeTab];
  const activePagination = {
    standard: standardPagination,
    second_opinion: soPagination,
    homecare: hcPagination,
  }[activeTab];
  const activePaginationSetter = {
    standard: setStandardPagination,
    second_opinion: setSoPagination,
    homecare: setHcPagination,
  }[activeTab];

  const handleRowClick = useCallback(
    (record: MedicalRecord) => {
      if (record.service === 'homecare') {
        navigate(`/homecare/reservations/${record.id}`);
        return;
      }
      setSelectedRecord(record);
      setPreviewRecord(record);
    },
    [navigate],
  );

  const handlePrint = useCallback((record: MedicalRecord) => {
    setSelectedRecord(record);
    setPreviewRecord(record);
    setShouldPrint(true);
  }, []);

  const columns: ColumnDef<MedicalRecord>[] = useMemo(() => {
    const isHomecare = activeTab === 'homecare';
    return [
      {
        accessorKey: 'recordNumber',
        header: t('medicalRecords.recordNumber'),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            <span>{row.getValue('recordNumber')}</span>
          </div>
        ),
      },
      {
        id: 'provider',
        header: isHomecare
          ? t('medicalRecords.vendor')
          : t('medicalRecords.doctor'),
        cell: ({ row }) => {
          if (isHomecare) {
            return (
              <span className="font-medium">
                {row.original.providerName || t('common.notSpecified')}
              </span>
            );
          }
          const doctor = row.original.doctor;
          if (!doctor) return <span className="text-gray-600">N/A</span>;
          return (
            <span className="font-medium">{`${doctor.firstName} ${doctor.lastName}`}</span>
          );
        },
      },
      {
        id: 'detail',
        header: isHomecare
          ? t('medicalRecords.service')
          : t('medicalRecords.detail'),
        cell: ({ row }) => (
          <span>
            {getLocalizedSpeciality(row.original.speciality, language).name}
          </span>
        ),
      },
      {
        accessorKey: 'date',
        header: t('medicalRecords.date'),
        cell: ({ row }) => (
          <span className="text-gray-600">{formatDate(row.original.date)}</span>
        ),
      },
      {
        id: 'actions',
        header: t('medicalRecords.actions'),
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(record);
                }}
                title={t('medicalRecords.view')}
              >
                <Eye className="h-4 w-4" />
              </Button>
              {record.service !== 'homecare' && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrint(record);
                    }}
                    title={t('medicalRecords.print')}
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                  {record.service === 'standard' ? (
                    <ReportDownloadButtons record={record} />
                  ) : (
                    <DownloadRecordButton record={record} />
                  )}
                </>
              )}
            </div>
          );
        },
      },
    ];
  }, [activeTab, handlePrint, handleRowClick, language, t]);

  const table = useReactTable({
    data: activeRecords,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: activePaginationSetter,
    manualPagination: true,
    pageCount: activePageCount,
    state: { pagination: activePagination },
    getRowId: (row) => row.id,
  });

  return (
    <>
      <div className="print:hidden">
        <Header>
          <SearchComponent />
          <ProfileCard />
        </Header>
        <div className="mb-4 flex-1 space-y-6 p-4 md:p-0 md:pe-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold">
              {t('medicalRecords.pageTitle')}
            </h1>
          </div>

          <div className="flex gap-2">
            {tabs
              .filter((tab) => tab.enabled !== false)
              .map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setActiveTab(tab.key);
                  setPreviewRecord(null);
                  setSelectedRecord(null);
                }}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          <div
            className={`grid ${previewRecord ? 'grid-cols-2' : 'grid-cols-1'}`}
          >
            <Card>
              <CardContent>
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <TableHead key={header.id} className="text-start">
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {activeLoading ? (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length}
                            className="h-24 text-center"
                          >
                            <Loading />
                          </TableCell>
                        </TableRow>
                      ) : table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-state={row.getIsSelected() && 'selected'}
                            className={`cursor-pointer hover:bg-gray-50 ${
                              previewRecord?.id === row.original.id
                                ? 'border-s-ring border-s-4 bg-[#EEFCF3]'
                                : ''
                            }`}
                            onClick={() => handleRowClick(row.original)}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id} className="text-start">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length}
                            className="h-24 text-center"
                          >
                            {t('medicalRecords.empty')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  <div className="flex justify-center pt-4">
                    <Pagination
                      currentPage={table.getState().pagination.pageIndex}
                      pageCount={activePageCount}
                      onPageChange={table.setPageIndex}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <ReportPreview
              record={previewRecord}
              shouldPrint={shouldPrint}
              onClose={() => setPreviewRecord(null)}
              onPrintDone={() => setShouldPrint(false)}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function ReportPreview({
  record,
  shouldPrint,
  onClose,
  onPrintDone,
}: {
  record: MedicalRecord | null;
  shouldPrint: boolean;
  onClose: () => void;
  onPrintDone: () => void;
}) {
  const { t } = useTranslation();
  const targetRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const isSecondOpinion = record?.service === 'second_opinion';

  const { data: diagnosisData } = useData(
    'GET /reservations/{id}/diagnoses',
    { id: record?.id || '' },
    { enabled: !!record?.id && record?.service === 'standard' },
  );

  const { data: reservationData } = useData(
    'GET /reservations/{id}',
    { id: record?.id || '' },
    { enabled: !!record?.id && record?.service === 'standard' },
  );

  const { data: secondOpinionData } = useData(
    'GET /second-opinions/{id}',
    { id: record?.id || '' },
    { enabled: !!record?.id && isSecondOpinion },
  );

  const reservationCreated = reservationData?.timeline.find(
    (it) => it.type === 'RESERVATION_CREATED',
  );
  const secondOpinionCreated = secondOpinionData?.timeline.find(
    (it) => it.type === 'CREATED',
  );
  const contextData = isSecondOpinion
    ? secondOpinionCreated?.context
    : reservationCreated?.context;
  const parsedContext = JSON.parse(contextData || '{}') as {
    patient: GetCurrentUser;
  };

  const generatedReportData = useAppointmentReportData({
    diagnoses: diagnosisData,
    reservation: reservationData,
    secondOpinion: secondOpinionData,
    patient: parsedContext.patient,
  });

  useEffect(() => {
    if (shouldPrint && generatedReportData && printRef.current) {
      const timer = setTimeout(() => {
        window.print();
        onPrintDone();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldPrint, generatedReportData, onPrintDone]);

  if (!record) return null;

  if (!generatedReportData) return <Loading />;

  return (
    <>
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          title={t('medicalRecords.close')}
          className="absolute top-2 z-50 bg-white shadow-md hover:bg-gray-50 ltr:right-8 rtl:left-8"
        >
          <X className="h-6 w-6" />
        </Button>

        <CardContent>
          <div className="max-h-[800px] overflow-auto rounded-lg border bg-white">
            <div ref={targetRef}>
              <AppointmentReport data={generatedReportData} />
            </div>
          </div>
        </CardContent>
      </div>

      {createPortal(
        <div
          ref={printRef}
          className="hidden print:absolute print:top-0 print:left-0 print:block print:w-full"
        >
          <AppointmentReport data={generatedReportData} />
        </div>,
        document.body,
      )}
    </>
  );
}

function DownloadRecordButton({ record }: { record: MedicalRecord }) {
  const { t } = useTranslation();
  const endpoint =
    record.service === 'standard'
      ? ('GET /reservations/{id}/report.pdf' as const)
      : ('GET /second-opinions/{id}/report.pdf' as const);
  const prefix =
    record.service === 'standard' ? 'medical-report' : 'second-opinion-report';

  return (
    <DownloadButton
      endpoint={endpoint}
      input={{ id: record.id }}
      filename={`${prefix}-${record.recordNumber}.pdf`}
      variant="ghost"
      size="sm"
      title={t('medicalRecords.download')}
      icon={<Download className="h-4 w-4" />}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

export function ReportDownloadButtons({ record }: { record: MedicalRecord }) {
  const { i18n, t } = useTranslation();
  const reports = record.reportAvailability ?? {
    prescription: false,
    labTests: false,
    diagnosis: false,
  };

  return (
    <DropdownMenu dir={i18n.dir()}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title={t('medicalRecords.download')}
          aria-label={t('medicalRecords.download')}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup className="grid">
          <DropdownMenuItem asChild disabled={!reports.prescription}>
            <DownloadButton
              endpoint="GET /reservations/{id}/prescription.pdf"
              input={{ id: record.id }}
              filename={`prescription-${record.recordNumber}.pdf`}
              variant="ghost"
              disabled={!reports.prescription}
              label={t('medicalRecords.reports.prescription')}
              title={t('medicalRecords.reports.prescription')}
              icon={<Pill className="h-4 w-4" />}
              className="w-full flex-row-reverse justify-end px-2 text-start"
              onClick={(e) => e.stopPropagation()}
            />
          </DropdownMenuItem>
          <DropdownMenuItem asChild disabled={!reports.labTests}>
            <DownloadButton
              endpoint="GET /reservations/{id}/labtests.pdf"
              input={{ id: record.id }}
              filename={`labtests-${record.recordNumber}.pdf`}
              variant="ghost"
              disabled={!reports.labTests}
              label={t('medicalRecords.reports.labTests')}
              title={t('medicalRecords.reports.labTests')}
              icon={<FlaskConical className="h-4 w-4" />}
              className="w-full flex-row-reverse justify-end px-2 text-start"
              onClick={(e) => e.stopPropagation()}
            />
          </DropdownMenuItem>
          <DropdownMenuItem asChild disabled={!reports.diagnosis}>
            <DownloadButton
              endpoint="GET /reservations/{id}/diagnosis.pdf"
              input={{ id: record.id }}
              filename={`diagnosis-${record.recordNumber}.pdf`}
              variant="ghost"
              disabled={!reports.diagnosis}
              label={t('medicalRecords.reports.diagnosis')}
              title={t('medicalRecords.reports.diagnosis')}
              icon={<Stethoscope className="h-4 w-4" />}
              className="w-full flex-row-reverse justify-end px-2 text-start"
              onClick={(e) => e.stopPropagation()}
            />
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
